import type { TFunction } from 'i18next';
import type { Tab } from '@repo/ui/components/molecules/data-table-filter-tabs';
import type { BackofficeTenantStatus } from '@repo/utils';
import type { FetchParams } from '@/api/models';
import type {
  TenantFilterDto,
  TenantStatusCounts,
  TenantStatusCountsParams,
} from '@/api/tenants/models';
import {
  type ApiParamReaders,
  pickApiParams,
  SEARCH_AND_SORT_PARAMS,
  valueParam,
} from '@/lib/helpers/list-api-params';
import {
  resolveDomainTab,
  type TabCounts,
  toKnownSubTab,
  type TranslatableDomainTab,
  translateFilterTabs,
  withTabCounts,
} from '@/lib/helpers/list-tabs';
import type { TenantsSearch } from '@/routes/-shared/tenantsSearchSchema';

export const TENANTS_ALL_TAB = 'all';
export const TENANTS_STATISTICS_TAB = 'statistics';

/**
 * Tab della lista: Attivi, Tutti, gli altri stati e la vista Statistiche.
 * Il filtro va all'API come `status`; il
 * tab «Tutti» non lo fissa. Il contatore lo portano Demo (neutro), In
 * scadenza (warning), Scaduti e In eliminazione (danger); Disabilitati e
 * Tutti no, come nel design.
 */
export const TENANTS_STATUS_TABS: readonly TranslatableDomainTab<BackofficeTenantStatus>[] =
  [
    { value: 'active', text: 'tenants.tabs.active', filter: 'active' },
    { value: TENANTS_ALL_TAB, text: 'tenants.tabs.all' },
    {
      value: 'demo',
      text: 'tenants.tabs.demo',
      filter: 'demo',
      countTone: 'neutral',
    },
    {
      value: 'expiring',
      text: 'tenants.tabs.expiring',
      filter: 'expiring',
      countTone: 'warning',
    },
    {
      value: 'expired',
      text: 'tenants.tabs.expired',
      filter: 'expired',
      countTone: 'danger',
    },
    { value: 'disabled', text: 'tenants.tabs.disabled', filter: 'disabled' },
    {
      value: 'deleting',
      text: 'tenants.tabs.deleting',
      filter: 'deleting',
      countTone: 'danger',
    },
  ];

const STATISTICS_TAB: TranslatableDomainTab<BackofficeTenantStatus> = {
  value: TENANTS_STATISTICS_TAB,
  text: 'tenants.tabs.statistics',
};

/** Il venditore vede Attivi, Tutti, Demo, In scadenza e Scaduti. */
const REFERRER_TAB_COUNT = 5;

/** I tab dell'admin: tutti gli stati e le statistiche. */
const ALL_TABS = [...TENANTS_STATUS_TABS, STATISTICS_TAB];

/** Gli stati che hanno un contatore nella pill del tab. */
const COUNTED_TABS = ['demo', 'expiring', 'expired', 'deleting'] as const;

/** I tab dell'area: l'admin ha tutti gli stati e le statistiche. */
export function getTenantsTabs(
  isAdminArea: boolean
): readonly TranslatableDomainTab<BackofficeTenantStatus>[] {
  return isAdminArea
    ? ALL_TABS
    : TENANTS_STATUS_TABS.slice(0, REFERRER_TAB_COUNT);
}

interface ResolvedTenantsTab {
  activeTab: string;
  /** Stato per l'API; assente su «Tutti» e sulle statistiche. */
  status: BackofficeTenantStatus | undefined;
  isStatistics: boolean;
}

/**
 * Il tab dell'URL se esiste nell'area, altrimenti «Tutti»: un tab
 * dell'admin aperto dal venditore (es. `subTab=deleting`) ricade sulla
 * lista intera.
 */
export function resolveTenantsTab(
  subTab: unknown,
  isAdminArea: boolean
): ResolvedTenantsTab {
  const { activeTab, filter } = resolveDomainTab({
    tabs: getTenantsTabs(isAdminArea),
    subTab,
    defaultTab: TENANTS_ALL_TAB,
  });
  return {
    activeTab,
    status: filter,
    isStatistics: activeTab === TENANTS_STATISTICS_TAB,
  };
}

/** Il vuoto dei tab di stato, con le parole del tab. */
export const TENANTS_EMPTY_TAB_TITLE_KEYS = {
  active: 'tenants.list.empty.tabs.active',
  demo: 'tenants.list.empty.tabs.demo',
  expiring: 'tenants.list.empty.tabs.expiring',
  expired: 'tenants.list.empty.tabs.expired',
  disabled: 'tenants.list.empty.tabs.disabled',
  deleting: 'tenants.list.empty.tabs.deleting',
} as const satisfies Record<string, string>;

/** Parametri che le statistiche non conoscono: l'API risponde 400. */
const LIST_ONLY_PARAMS = {
  hasCassettoFiscale: undefined,
  planId: undefined,
  referrerId: undefined,
  sort: undefined,
};

interface TenantsFilterTabsOptions {
  t: TFunction;
  counts: TenantStatusCounts | undefined;
  isAdminArea: boolean;
  /** La vista aperta è Statistiche: tornando a uno stato si azzera l'ordine. */
  isStatisticsView: boolean;
}

/**
 * I tab per `filterTabs`, con etichetta, contatore e i parametri da
 * azzerare: verso le statistiche filtri e ordinamento della lista (400),
 * dalle statistiche l'ordinamento (le colonne sono altre). Fra due stati
 * filtri e ordinamento restano.
 */
export function buildTenantsFilterTabs({
  t,
  counts,
  isAdminArea,
  isStatisticsView,
}: TenantsFilterTabsOptions): Omit<Tab, 'onClick'>[] {
  const tabs = withTabCounts(
    translateFilterTabs(getTenantsTabs(isAdminArea), t),
    buildTenantTabCounts(counts)
  );
  return tabs.map((tab) => {
    if (tab.value === TENANTS_STATISTICS_TAB) {
      return { ...tab, params: LIST_ONLY_PARAMS };
    }
    return isStatisticsView ? { ...tab, params: { sort: undefined } } : tab;
  });
}

/** Contatori dei tab dagli stati del server; assenti mentre caricano. */
export function buildTenantTabCounts(
  counts: TenantStatusCounts | undefined
): TabCounts | undefined {
  if (!counts) return undefined;
  return Object.fromEntries(
    COUNTED_TABS.map((status) => [status, counts[status]])
  );
}

/**
 * I parametri dei link di prima (dashboard, segnalibri) che lo schema della
 * rotta traduce in `subTab` (e non fa uscire dalla search), poi tolti
 * dall'URL. Unica fonte per lo schema, `toLegacyTenantsTab` e la pulizia
 * dell'URL.
 *
 * TODO(2026-12-31): rimuovere i parametri vecchi con tutto ciò che li legge.
 */
export const LEGACY_TENANTS_SEARCH_KEYS = [
  'status',
  'isActive',
  'planStatus',
  'tab',
] as const;

export type LegacyTenantsSearchKey =
  (typeof LEGACY_TENANTS_SEARCH_KEYS)[number];

const LEGACY_PLAN_STATUSES: readonly string[] = ['expiring', 'expired'];

type LegacyTenantsTabOptions = Record<
  LegacyTenantsSearchKey,
  string | null | undefined
> & {
  /** I tab che l'area conosce: un tab dell'admin non vale per il referrer. */
  isAdminArea: boolean;
  subTab: string | null | undefined;
};

/**
 * Il tab dai parametri di prima: un `subTab` dell'area vince; poi
 * `?status=<tab>`, `isActive=false` (Disabilitati, che nel backend vince
 * sulla scadenza), `planStatus=expiring|expired` e `tab=statistics`. Un
 * valore che l'area non conosce non accende nulla (resta «Tutti», e la
 * barra dei tab non resta senza un tab acceso). Parametri vecchi: vedi
 * `LEGACY_TENANTS_SEARCH_KEYS`.
 */
export function toLegacyTenantsTab({
  isAdminArea,
  subTab,
  status,
  isActive,
  planStatus,
  tab,
}: LegacyTenantsTabOptions): string | undefined {
  const tabs = getTenantsTabs(isAdminArea);
  const legacy =
    (isActive === 'false' && 'disabled') ||
    (planStatus && LEGACY_PLAN_STATUSES.includes(planStatus) && planStatus) ||
    (tab === TENANTS_STATISTICS_TAB && TENANTS_STATISTICS_TAB) ||
    undefined;
  return [subTab, status, legacy]
    .map((candidate) => toKnownSubTab(tabs, candidate))
    .find((known) => known !== undefined);
}

/**
 * L'URL (la query grezza, `?status=expired&…`) porta ancora un parametro di
 * prima: tradotto dallo schema, va tolto anche dalla barra degli indirizzi,
 * altrimenti resta lì accanto al `subTab` che lo ha sostituito.
 */
export function hasLegacyTenantsSearchParams(searchStr: string): boolean {
  const params = new URLSearchParams(searchStr);
  return LEGACY_TENANTS_SEARCH_KEYS.some((key) => params.has(key));
}

const CLEARED_LEGACY_TENANTS_SEARCH = Object.fromEntries(
  LEGACY_TENANTS_SEARCH_KEYS.map((key) => [key, undefined])
) as Record<LegacyTenantsSearchKey, undefined>;

/**
 * La search da riscrivere nell'URL senza i parametri di prima. Vanno azzerati
 * esplicitamente: la search di una rotta (`useSearch`) somma a quella
 * validata le chiavi grezze dell'URL, comprese quelle che lo schema non
 * restituisce, e una navigazione con quella search le riscriverebbe.
 */
export function withoutLegacyTenantsSearchParams<TSearch extends object>(
  search: TSearch
): TSearch & Record<LegacyTenantsSearchKey, undefined> {
  return { ...search, ...CLEARED_LEGACY_TENANTS_SEARCH };
}

type ListParams = Omit<FetchParams, 'skip' | 'take'>;
type TenantsApiParams = ListParams &
  Pick<TenantFilterDto, 'hasCassettoFiscale' | 'planId' | 'referrerId'>;

/** Campi di `GET backoffice/tenants`; il tab si aggiunge come `status`. */
const TENANTS_API_PARAMS: ApiParamReaders<TenantsSearch, TenantsApiParams> = {
  ...SEARCH_AND_SORT_PARAMS,
  hasCassettoFiscale: valueParam,
  planId: valueParam,
  referrerId: valueParam,
};

/** I contatori seguono ricerca e filtri della lista, non tab né ordine. */
const TENANT_STATUS_COUNTS_API_PARAMS: ApiParamReaders<
  TenantsSearch,
  TenantStatusCountsParams
> = {
  search: SEARCH_AND_SORT_PARAMS.search,
  hasCassettoFiscale: valueParam,
  planId: valueParam,
  referrerId: valueParam,
};

/** `GET backoffice/tenants/statistics` accetta solo ricerca e ordinamento. */
const TENANT_STATISTICS_API_PARAMS: ApiParamReaders<TenantsSearch, ListParams> =
  SEARCH_AND_SORT_PARAMS;

export function buildTenantsApiParams(
  search: TenantsSearch,
  status: BackofficeTenantStatus | undefined
): TenantsApiParams & Pick<TenantFilterDto, 'status'> {
  return {
    ...pickApiParams(search, TENANTS_API_PARAMS),
    ...(status && { status }),
  };
}

export function buildTenantStatusCountsApiParams(
  search: TenantsSearch
): TenantStatusCountsParams {
  return pickApiParams(search, TENANT_STATUS_COUNTS_API_PARAMS);
}

export function buildTenantStatisticsApiParams(
  search: TenantsSearch
): ListParams {
  return pickApiParams(search, TENANT_STATISTICS_API_PARAMS);
}
